import React, { useState, useEffect, useRef, useCallback } from 'react';
import ReactDOM from 'react-dom';
import {
  ChevronDownIcon,
  BuildingStorefrontIcon,
  UserGroupIcon,
  CheckIcon,
  ArrowsRightLeftIcon
} from '@heroicons/react/24/outline';
import { useNavigate } from 'react-router-dom';
import { useBusiness } from '../../shared/contexts/BusinessContext';
import { useAuthBusiness, getNormalizedSector } from '../../contexts/AuthBusinessContext';
import { collection, query, where, getDocs, doc, getDoc } from 'firebase/firestore';
import { db } from '../../services/firebase';
import { hibritService } from '../../services/hibritService';

const BusinessSwitcher = ({ darkMode }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [currentBusiness, setCurrentBusiness] = useState(null);
  const [firebaseBusinesses, setFirebaseBusinesses] = useState([]);
  const [firebaseLoading, setFirebaseLoading] = useState(true);
  const [dropdownPos, setDropdownPos] = useState({ top: 0, right: 0 });
  const dropdownRef = useRef(null);
  const buttonRef = useRef(null);
  const navigate = useNavigate();

  // Default logo path for businesses without logo (from public folder to avoid CORS)  
  const DEFAULT_LOGO = '/default-logo.svg';

  // Use BusinessContext instead of separate state - but prioritize Firebase businesses
  const { businesses, loading } = useBusiness();

  // Use AuthBusinessContext for proper sidebar management
  const { selectBusiness: authSelectBusiness, selectedBusiness, userBusinesses, currentUser, currentUserRole } = useAuthBusiness();

  // We use currentUser and userRole from useAuthBusiness directly
  // const [user, setUser] = useState(JSON.parse(localStorage.getItem('user') || '{}'));
  // const [userEmail, setUserEmail] = useState(user.email || '');

  // Initialize currentBusiness from localStorage on first load
  useEffect(() => {
    const savedBusiness = JSON.parse(localStorage.getItem('selectedBusiness') || 'null');
    if (savedBusiness && !currentBusiness) {
      setCurrentBusiness(savedBusiness);
    }
  }, []);

  // Sync with localStorage removed - relying on AuthBusinessContext re-renders
  /*
  useEffect(() => {
    const handleStorageChange = () => {
      const updatedUser = JSON.parse(localStorage.getItem('user') || '{}');
      setUser(updatedUser);
      setUserEmail(updatedUser.email || '');
      console.log('🔍 BusinessSwitcher - User updated:', updatedUser);
    };

    window.addEventListener('localStorageChange', handleStorageChange);
    window.addEventListener('authStateChanged', handleStorageChange);

    return () => {
      window.removeEventListener('localStorageChange', handleStorageChange);
      window.removeEventListener('authStateChanged', handleStorageChange);
    };
  }, []);
  */

  // Reduced console logging for performance
  // console.log('🔍 BusinessSwitcher - Current User:', user);

  // Load businesses from context instead of independent fetch to ensure synchronization
  useEffect(() => {
    if (userBusinesses && userBusinesses.length > 0) {
      setFirebaseBusinesses(userBusinesses);
      setFirebaseLoading(false);
    } else if (userBusinesses) {
      setFirebaseLoading(false);
    }
  }, [userBusinesses]);

  useEffect(() => {
    // Use selectedBusiness from AuthBusinessContext as the source of truth
    const localSelectedBusiness = JSON.parse(localStorage.getItem('selectedBusiness') || 'null');

    if (selectedBusiness) {
      setCurrentBusiness(selectedBusiness);
    } else {
      // If no business selected, we are in Central Management mode (Owners only)
      // Cari users should default to a placeholder or wait for auto-selection
      const isCariUser = currentUserRole === 'cari' || currentUser?.role === 'cari';
      
      setCurrentBusiness({
        id: 'business_account',
        name: isCariUser ? 'İşletme' : 'Hesap Yönetimi',
        type: 'business_account',
        business_type: 'management'
      });
    }

    // Close dropdown when clicking outside (check both button and portal dropdown)
    const handleClickOutside = (event) => {
      const clickedInsideButton = buttonRef.current && buttonRef.current.contains(event.target);
      const clickedInsideDropdown = dropdownRef.current && dropdownRef.current.contains(event.target);
      if (!clickedInsideButton && !clickedInsideDropdown) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [selectedBusiness, currentUser?.brandName]);

  // Calculate dropdown position from button bounding rect
  const updateDropdownPosition = useCallback(() => {
    if (buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setDropdownPos({
        top: rect.bottom + 8, // 8px gap below button
        right: Math.max(12, window.innerWidth - rect.right), // align right edge with button
      });
    }
  }, []);

  useEffect(() => {
    if (isOpen) {
      updateDropdownPosition();
      // Recalculate on scroll/resize
      window.addEventListener('scroll', updateDropdownPosition, true);
      window.addEventListener('resize', updateDropdownPosition);
      return () => {
        window.removeEventListener('scroll', updateDropdownPosition, true);
        window.removeEventListener('resize', updateDropdownPosition);
      };
    }
  }, [isOpen, updateDropdownPosition]);


  // Map Firebase sector IDs to sector names and normalize sector names
  const getSectorNameFromId = (sectorId) => {
    // Handle Firebase ID mappings
    const sectorMap = {
      '9Hi9XsqIEp0k5cLCtSaM': 'food-beverage', // Yeme-İçme
      'jC8leOrZ5D9jycx1fknk': 'retail',        // Perakende  
      'm9q78OoqRzawqJhnG4yW': 'service'        // Hizmet
    };

    // If it's a Firebase ID, map it
    if (sectorMap[sectorId]) {
      return sectorMap[sectorId];
    }

    // Normalize direct sector names
    if (sectorId === 'food_beverage') return 'food-beverage';
    if (sectorId === 'food-beverage') return 'food-beverage';

    return sectorId || 'general';
  };

  const handleBusinessSwitch = async (business) => {
    console.log('🔄 BusinessSwitcher: Switching to business:', business.name, 'ID:', business.id);

    // Close dropdown immediately for better UX
    setIsOpen(false);

    // Update local state immediately for visual feedback
    setCurrentBusiness(business);

    try {
      // Use AuthBusinessContext's selectBusiness for proper state management
      if (authSelectBusiness) {
        console.log('🎯 BusinessSwitcher: Using AuthBusinessContext.selectBusiness');
        const targetPath = await authSelectBusiness(business);

        // Navigate to the returned path
        console.log('📍 BusinessSwitcher: Navigating to:', targetPath);
        navigate(targetPath);
      } else {
        throw new Error('AuthBusinessContext not initialized');
      }
    } catch (error) {
      console.error('❌ Business switch failed:', error);
      alert('İşletme değiştirilemedi. Lütfen tekrar deneyin.');

      // Revert local state on error
      const savedBusiness = JSON.parse(localStorage.getItem('selectedBusiness') || 'null');
      if (savedBusiness) {
        setCurrentBusiness(savedBusiness);
      }
    }
  };

  const getBusinessIcon = (business) => {
    if (business.id === 'business_account' || business.business_type === 'management') {
      return '🏢';
    }

    const normSector = getNormalizedSector(business);
    switch (normSector) {
      case 'hospitality': return '🏨';
      case 'food-beverage': return '🍽️';
      case 'retail': return '🏪';
      case 'service': return '💄';
      default: return '🏢';
    }
  };

  const getBusinessTypeLabel = (business) => {
    if (business.id === 'business_account' || business.business_type === 'management') {
      return 'Hesap Yönetimi';
    }

    const normSector = getNormalizedSector(business);
    switch (normSector) {
      case 'hospitality': return 'Otelcilik';
      case 'food-beverage': return 'Yeme-İçme';
      case 'retail': return 'Perakende';
      case 'service': return 'Hizmet';
      default: return 'İşletme';
    }
  };

  // ✅ Kesin Tekilleştirme (Deduplication) logic at component level
  const uniqueBusinessesMap = new Map();
  firebaseBusinesses.forEach(b => {
    if (b && b.id && !uniqueBusinessesMap.has(b.id)) {
      uniqueBusinessesMap.set(b.id, b);
    }
  });
  
  const uniqueBusinesses = Array.from(uniqueBusinessesMap.values());

  // Add business account option only for REAL business owners (NOT for Cari users or staff)
  const isCari = currentUserRole === 'cari' || currentUser?.role === 'cari';

  // Personel rolleri — bu roller Hesap Yönetimi göremez
  const STAFF_ROLES_SW = ['cashier', 'waiter', 'kitchen_staff', 'courier', 'employee', 'staff'];
  const effectiveRole = (currentUserRole || currentUser?.role || currentUser?.user_type || '').toLowerCase();
  const isStaff = STAFF_ROLES_SW.includes(effectiveRole);

  const isOwner = !isCari && !isStaff && (
    effectiveRole === 'owner' ||
    effectiveRole === 'business_owner' ||
    effectiveRole === 'system_admin' ||
    effectiveRole === 'super_admin' ||
    (userBusinesses || []).some(m => {
      const mRole = (m.role || '').toLowerCase();
      return mRole === 'owner' || mRole === 'business_owner';
    })
  );
  
  const availableBusinesses = isOwner ? [
    {
      id: 'business_account',
      type: 'business_account',
      name: 'Hesap Yönetimi',
      description: 'İşletme yönetim paneli',
      business_type: 'management',
      icon: '🏢'
    },
    ...uniqueBusinesses
  ] : uniqueBusinesses;

  // 🛡️ MOBILE STABILITY: Always show interactive switcher if loading or has businesses
  // Only hide if strictly for brand employees who don't have multiple business access
  if (currentUser?.userType === 'brand_employee' || currentUser?.role === 'brand_employee') {
    const bizLogo = currentBusiness?.logo || DEFAULT_LOGO;
    const initial = (currentBusiness?.name || currentUser?.brandName || 'İ').charAt(0).toUpperCase();

    return (
      <div className="flex items-center space-x-3 p-2">
        <div className="w-10 h-10 rounded-xl overflow-hidden shadow-sm border border-gray-200">
          {(currentBusiness?.logo) ? (
            <img
              src={bizLogo}
              alt={currentBusiness?.name || currentUser?.brandName || 'İşletme'}
              className="w-full h-full object-cover bg-white"
              onError={(e) => e.target.style.display = 'none'}
            />
          ) : (
             <div className="w-full h-full bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center text-white font-black text-lg">
                {initial}
             </div>
          )}
        </div>
        <div className="hidden sm:block text-left flex-1 min-w-0">
          <div className="text-sm font-semibold text-gray-900 truncate">
            {currentBusiness?.name || currentUser?.brandName || 'İşletme'}
          </div>
          <div className="text-xs text-gray-500 truncate">
            {currentBusiness ? getBusinessTypeLabel(currentBusiness) : 'Panel'}
          </div>
        </div>
      </div>
    );
  }

  // Portal dropdown content
  const dropdownContent = isOpen ? ReactDOM.createPortal(
    <div 
      ref={dropdownRef}
      className="rounded-xl shadow-2xl border py-2 border-gray-200 dark:border-gray-700"
      style={{
        position: 'fixed',
        top: dropdownPos.top,
        right: dropdownPos.right,
        width: window.innerWidth < 640 ? 'calc(100vw - 24px)' : '288px',
        maxWidth: '320px',
        zIndex: 999999,
        backgroundColor: darkMode ? '#1f2937' : '#ffffff',
        opacity: 1,
        isolation: 'isolate',
        boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
      }}
    >
      <div className="px-4 py-2 border-b" style={{ borderColor: darkMode ? '#374151' : '#f3f4f6' }}>
        <div className="text-xs font-medium uppercase tracking-wide" style={{ color: darkMode ? '#9ca3af' : '#6b7280' }}>
          Erişilebilir İşletmeler
        </div>
        <div className="text-xs mt-1" style={{ color: darkMode ? '#6b7280' : '#9ca3af' }}>
          {availableBusinesses.length} alan mevcut
        </div>
      </div>

      {loading || firebaseLoading ? (
        <div className="px-4 py-3 text-center">
          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600 mx-auto"></div>
          <span className="text-xs text-gray-500 mt-2">Yükleniyor...</span>
        </div>
      ) : (
        <div className="max-h-80 overflow-y-auto">
          {availableBusinesses.map((business) => {
            const isSelected = currentBusiness?.id === business.id;
            const displayLogo = (isSelected && currentBusiness?.logo) ? currentBusiness.logo : business.logo;

            return (
              <button
                key={business.id}
                onClick={() => handleBusinessSwitch(business)}
                className="w-full text-left px-4 py-3 transition-colors duration-200 flex items-center justify-between"
                style={{ backgroundColor: 'transparent' }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = darkMode ? '#374151' : '#f9fafb'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
              >
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 rounded-lg overflow-hidden border" style={{ borderColor: darkMode ? '#374151' : '#f3f4f6' }}>
                    {business.id === 'business_account' ? (
                      <div className="w-full h-full bg-gradient-to-br from-purple-600 to-indigo-700 flex items-center justify-center text-white font-black text-lg">
                        H
                      </div>
                    ) : displayLogo ? (
                      <img
                        src={displayLogo}
                        alt={business.name}
                        className="w-full h-full object-cover"
                        style={{ backgroundColor: '#fff' }}
                        onError={(e) => e.target.style.display = 'none'}
                      />
                    ) : (
                      <div className="w-full h-full bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center text-white font-black text-lg">
                        {(business.name || 'İ').charAt(0).toUpperCase()}
                      </div>
                    )}
                  </div>
                  <div>
                    <div className="text-sm font-medium" style={{ color: darkMode ? '#f3f4f6' : '#111827' }}>
                      {business.name}
                    </div>
                    <div className="text-xs flex items-center space-x-2" style={{ color: darkMode ? '#9ca3af' : '#6b7280' }}>
                      <span>{getBusinessTypeLabel(business)}</span>
                      {business.type === 'business_account' && (
                        <span className="bg-purple-100 text-purple-600 px-2 py-0.5 rounded-full text-xs font-medium">
                          Panel
                        </span>
                      )}
                    </div>
                    {business.description && (
                      <div className="text-xs mt-1" style={{ color: darkMode ? '#6b7280' : '#9ca3af' }}>
                        {business.description}
                      </div>
                    )}
                  </div>
                </div>
                {currentBusiness?.id === business.id && (
                  <CheckIcon className="w-4 h-4 text-blue-600" />
                )}
              </button>
            );
          })}
        </div>
      )}

      <div className="px-4 py-2" style={{ borderTop: `1px solid ${darkMode ? '#374151' : '#f3f4f6'}` }}>
        <div className="text-xs text-center" style={{ color: darkMode ? '#6b7280' : '#9ca3af' }}>
          {currentUser?.name || currentUser?.full_name || currentUser?.email?.split('@')[0] || 'Kullanıcı'} • {currentUserRole === 'business_owner' ? 'İşletme Sahibi' :
            currentUserRole === 'manager' ? 'Yönetici' : 
            currentUserRole === 'cari' ? 'Cari Müşteri' : 'Kullanıcı'}
        </div>
      </div>
    </div>,
    document.body
  ) : null;

  return (
    <div className="relative">
      {/* ALWAYS RENDER BUTTON FOR INTERACTION IF AUTHENTICATED */}
      <button
        ref={buttonRef}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setIsOpen(!isOpen);
        }}
        className="flex items-center p-1.5 sm:p-2 rounded-lg hover:bg-gray-50 active:bg-gray-100 transition-colors duration-200 touch-manipulation relative"
      >
        <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg sm:rounded-xl overflow-hidden shadow-sm border border-gray-200">
          {(currentBusiness?.id === 'business_account' || currentBusiness?.type === 'business_account') ? (
            <div className="w-full h-full bg-gradient-to-br from-purple-600 to-indigo-700 flex items-center justify-center text-white font-black text-base sm:text-lg">
              H
            </div>
          ) : currentBusiness?.logo ? (
            <div className="relative w-full h-full">
               <img
                  src={currentBusiness.logo}
                  alt={currentBusiness?.name || 'İşletme Seç'}
                  className="w-full h-full object-cover bg-white"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.nextSibling.style.display = 'flex';
                  }}
              />
              <div className="hidden w-full h-full bg-gradient-to-br from-indigo-500 to-purple-600 items-center justify-center text-white font-black text-base sm:text-lg">
                  {(currentBusiness?.name || 'İ').charAt(0).toUpperCase()}
              </div>
            </div>
          ) : (
            <div className="w-full h-full bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center text-white font-black text-base sm:text-lg">
                {(currentBusiness?.name || 'İ').charAt(0).toUpperCase()}
            </div>
          )}
        </div>
        <div className="hidden md:block text-left ml-3 flex-1 min-w-0">
          <div className="text-sm font-semibold text-gray-900 truncate">
            {currentBusiness?.name || 'İşletme Seç'}
          </div>
          <div className="text-xs text-gray-500 flex items-center">
            <span className="truncate">{currentBusiness ? getBusinessTypeLabel(currentBusiness) : 'Panel Seçin'}</span>
            <ChevronDownIcon className="w-3 h-3 ml-1 flex-shrink-0" />
          </div>
        </div>
        <ChevronDownIcon className={`hidden md:block w-4 h-4 text-gray-400 ml-1 transition-transform duration-200 ${isOpen ? 'transform rotate-180' : ''
          }`} />
      </button>

      {/* Dropdown rendered via Portal to escape nav stacking context */}
      {dropdownContent}
    </div>
  );
};

export default BusinessSwitcher;